import React, { useState, useEffect } from 'react';
import { Share, ChevronDown, HelpCircle } from 'lucide-react';

const formatCurrency = (amount) => {
  // Convert number to string and handle decimals
  const numStr = Math.abs(amount).toFixed(0);
  
  // Split integer into parts based on Indian numbering system
  const lastThree = numStr.substring(numStr.length - 3);
  const otherNums = numStr.substring(0, numStr.length - 3);
  
  // Add commas according to Indian number system
  const formatted = otherNums !== '' 
    ? otherNums.replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + lastThree 
    : lastThree;
  
  return '₹' + (amount < 0 ? '-' : '') + formatted;
};

const formatPercentage = (value) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value / 100);
};

const PoorFundsCalculator = () => {
  const [values, setValues] = useState({
    currentInvestment: 1000000,
    currentFundReturn: 8,
    betterFundReturn: 12,
    investmentPeriod: 10,
    exitLoad: 1,
    switchCost: 0.5,
    taxRate: 10
  });

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showShareToast, setShowShareToast] = useState(false);
  const [showToolTip, setShowToolTip] = useState('');
  
  const handleChange = (key, value) => {
    setValues(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const calculateSwitchBenefit = () => {
    const { 
      currentInvestment, 
      currentFundReturn, 
      betterFundReturn,
      investmentPeriod,
      exitLoad,
      switchCost,
      taxRate
    } = values;
    
    // Calculate future value if staying with current fund
    const futureValueCurrentFund = currentInvestment * Math.pow(1 + (currentFundReturn / 100), investmentPeriod);
    
    // Calculate immediate costs of switching
    const exitLoadAmount = currentInvestment * (exitLoad / 100);
    const switchCostAmount = currentInvestment * (switchCost / 100);
    
    // Calculate capital gains (assuming some appreciation already)
    // For simplicity, we'll assume the investment has been held for 1 year with the same return
    const assumedGain = currentInvestment * (currentFundReturn / 100);
    const taxOnGain = assumedGain * (taxRate / 100);
    
    // Total immediate cost to switch
    const totalSwitchCost = exitLoadAmount + switchCostAmount + taxOnGain;
    
    // Net amount to invest in new fund
    const netInvestmentInNewFund = currentInvestment - totalSwitchCost;
    
    // Future value if switching to better fund
    const futureValueBetterFund = netInvestmentInNewFund * Math.pow(1 + (betterFundReturn / 100), investmentPeriod);
    
    // Calculate benefit of switching
    const absoluteBenefit = futureValueBetterFund - futureValueCurrentFund;
    const percentageBenefit = (absoluteBenefit / futureValueCurrentFund) * 100;
    
    // Calculate break-even point in years
    let breakEvenYears = 0;
    let tempCurrentValue = currentInvestment;
    let tempNewValue = netInvestmentInNewFund;
    
    while (tempNewValue <= tempCurrentValue && breakEvenYears < 50) {
      breakEvenYears++;
      tempCurrentValue = tempCurrentValue * (1 + (currentFundReturn / 100));
      tempNewValue = tempNewValue * (1 + (betterFundReturn / 100));
    }
    
    // Calculate yearly growth comparison
    const yearlyComparison = [];
    let currentFundValue = currentInvestment;
    let betterFundValue = netInvestmentInNewFund;
    
    for (let year = 1; year <= Math.min(investmentPeriod, 30); year++) {
      currentFundValue = currentFundValue * (1 + (currentFundReturn / 100));
      betterFundValue = betterFundValue * (1 + (betterFundReturn / 100));
      
      yearlyComparison.push({
        year,
        currentFundValue: Math.round(currentFundValue),
        betterFundValue: Math.round(betterFundValue),
        difference: Math.round(betterFundValue - currentFundValue)
      });
    }
    
    return {
      futureValueCurrentFund: Math.round(futureValueCurrentFund),
      futureValueBetterFund: Math.round(futureValueBetterFund),
      absoluteBenefit: Math.round(absoluteBenefit),
      percentageBenefit: Math.round(percentageBenefit * 100) / 100,
      breakEvenYears,
      exitLoadAmount: Math.round(exitLoadAmount),
      switchCostAmount: Math.round(switchCostAmount),
      taxOnGain: Math.round(taxOnGain),
      totalSwitchCost: Math.round(totalSwitchCost),
      netInvestmentInNewFund: Math.round(netInvestmentInNewFund),
      yearlyComparison
    };
  };

  const [result, setResult] = useState(calculateSwitchBenefit());

  useEffect(() => {
    setResult(calculateSwitchBenefit());
  }, [values]);

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShowShareToast(true);
      setTimeout(() => setShowShareToast(false), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  return (
    <div className="calculator-container pt-24">
      <div className="calculator-header text-center mb-8">
        <h1 className="text-2xl font-semibold text-[#113262] mb-2">Fund Switch Calculator</h1>
        <h2 className="text-lg text-gray-600">Evaluate the benefit of moving from poor funds to better performing funds</h2>
      </div>

      <div className="max-w-5xl mx-auto p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Input Sections */}
          <div className="lg:col-span-2 space-y-8">
            {/* Current Investment */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Current Investment Details</h2>
              <div className="grid grid-cols-1 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Current Investment Amount
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('currentInvestment')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'currentInvestment' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Your current investment amount in the poorly performing fund
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.currentInvestment}
                    onChange={(e) => handleChange('currentInvestment', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={10000}
                    max={10000000}
                    step={10000}
                    value={values.currentInvestment}
                    onChange={(e) => handleChange('currentInvestment', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>₹10K</span>
                    <span>₹1Cr</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Current Fund's Expected Return
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('currentReturn')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'currentReturn' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Expected annual return of your current fund
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.currentFundReturn}
                    onChange={(e) => handleChange('currentFundReturn', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={20}
                    step={0.1}
                    value={values.currentFundReturn}
                    onChange={(e) => handleChange('currentFundReturn', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1%</span>
                    <span>20%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* New Fund Details */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">New Fund Details</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Better Fund's Expected Return
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('betterReturn')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'betterReturn' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Expected annual return of the fund you wish to switch to
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.betterFundReturn}
                    onChange={(e) => handleChange('betterFundReturn', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={values.currentFundReturn}
                    max={30}
                    step={0.1}
                    value={values.betterFundReturn}
                    onChange={(e) => handleChange('betterFundReturn', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{values.currentFundReturn}%</span>
                    <span>30%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5">Investment Period (Years)</label>
                  <input
                    type="number"
                    value={values.investmentPeriod}
                    onChange={(e) => handleChange('investmentPeriod', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={values.investmentPeriod}
                    onChange={(e) => handleChange('investmentPeriod', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 year</span>
                    <span>30 years</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Switch Costs */}
            <div className="bg-white rounded-lg shadow p-4">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Switch Costs</h2>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Exit Load
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('exitLoad')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'exitLoad' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Fee charged when redeeming your current fund units
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.exitLoad}
                    onChange={(e) => handleChange('exitLoad', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={5}
                    step={0.1}
                    value={values.exitLoad}
                    onChange={(e) => handleChange('exitLoad', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>5%</span>
                  </div>
                </div>

                <div>
                  <label className="block font-medium text-sm mb-1.5 flex items-center">
                    Other Switch Costs
                    <div className="relative ml-1">
                      <HelpCircle 
                        size={14} 
                        className="text-gray-400 cursor-pointer"
                        onMouseEnter={() => setShowToolTip('switchCost')}
                        onMouseLeave={() => setShowToolTip('')}
                      />
                      {showToolTip === 'switchCost' && (
                        <div className="absolute z-10 left-6 -top-2 w-60 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                          Other costs such as transaction fees, entry load for new fund
                        </div>
                      )}
                    </div>
                  </label>
                  <input
                    type="number"
                    value={values.switchCost}
                    onChange={(e) => handleChange('switchCost', Number(e.target.value))}
                    className="w-full p-1.5 border rounded text-sm"
                  />
                  <input
                    type="range"
                    min={0}
                    max={3}
                    step={0.1}
                    value={values.switchCost}
                    onChange={(e) => handleChange('switchCost', Number(e.target.value))}
                    className="w-full mt-2"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>0%</span>
                    <span>3%</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Settings */}
            <div className="bg-white rounded-lg shadow">
              <button
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-50"
              >
                <h2 className="text-lg font-bold text-gray-900">Advanced Settings</h2>
                <ChevronDown 
                  className={`transform transition-transform ${showAdvanced ? 'rotate-180' : ''}`}
                  size={20}
                />
              </button>
              
              {showAdvanced && (
                <div className="p-4 border-t">
                  <div className="grid grid-cols-1 gap-6">
                    <div>
                      <label className="block font-medium text-sm mb-1.5 flex items-center">
                        Applicable Tax Rate
                        <div className="relative ml-1">
                          <HelpCircle 
                            size={14} 
                            className="text-gray-400 cursor-pointer"
                            onMouseEnter={() => setShowToolTip('taxRate')}
                            onMouseLeave={() => setShowToolTip('')}
                          />
                          {showToolTip === 'taxRate' && (
                            <div className="absolute z-10 left-6 -top-2 w-72 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                              Tax rate applicable on capital gains (typically 10-15% for long-term equity gains in India)
                            </div>
                          )}
                        </div>
                      </label>
                      <input
                        type="number"
                        value={values.taxRate}
                        onChange={(e) => handleChange('taxRate', Number(e.target.value))}
                        className="w-full p-1.5 border rounded text-sm"
                      />
                      <input
                        type="range"
                        min={0}
                        max={30}
                        step={1}
                        value={values.taxRate}
                        onChange={(e) => handleChange('taxRate', Number(e.target.value))}
                        className="w-full mt-2"
                      />
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>0%</span>
                        <span>30%</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Results Section */}
          <div className="bg-[#113262] text-white rounded-lg h-[520px] sticky top-6">
            <div className="p-4 border-b border-white/20">
              <div className="flex justify-between items-center">
                <h3 className="text-xl font-bold">Switch Benefit Analysis</h3>
                <button onClick={handleShare} className="p-1 hover:bg-blue-700 rounded">
                  <Share size={18} />
                </button>
              </div>
            </div>

            <div className="p-4 flex flex-col h-[calc(100%-68px)] justify-between">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-2xl font-bold mb-1">
                    {formatCurrency(result.absoluteBenefit)}
                  </div>
                  <div className="text-sm text-gray-300">Total Benefit from Switching</div>
                </div>

                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Percentage Benefit</span>
                    <span className="font-bold">{result.percentageBenefit}%</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Break-even Period</span>
                    <span className="font-bold">{result.breakEvenYears} years</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Current Fund Future Value</span>
                    <span className="font-bold">{formatCurrency(result.futureValueCurrentFund)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Better Fund Future Value</span>
                    <span className="font-bold">{formatCurrency(result.futureValueBetterFund)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Exit Load Cost</span>
                    <span className="font-bold">{formatCurrency(result.exitLoadAmount)}</span>
                  </div>

                  <div className="flex justify-between items-center py-2 border-t border-white/20">
                    <span className="text-sm">Other Switch Costs</span>
                    <span className="font-bold">{formatCurrency(result.switchCostAmount)}</span>
                  </div>

                  {showAdvanced && (
                    <>
                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Tax on Gains</span>
                        <span className="font-bold">{formatCurrency(result.taxOnGain)}</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Total Switch Cost</span>
                        <span className="font-bold">{formatCurrency(result.totalSwitchCost)}</span>
                      </div>

                      <div className="flex justify-between items-center py-2 border-t border-white/20">
                        <span className="text-sm">Net Investment in New Fund</span>
                        <span className="font-bold">{formatCurrency(result.netInvestmentInNewFund)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>

              <button className="w-full bg-orange-400 text-white py-2 rounded-lg mt-4 hover:bg-orange-500 transition-colors text-sm">
                Get Fund Recommendations →
              </button>
            </div>
          </div>

          {showShareToast && (
            <div className="fixed top-4 right-4 bg-green-600 text-white px-6 py-3 rounded shadow-lg">
              Link copied!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PoorFundsCalculator;